import { Config, ConfigProvider, Effect, Option } from 'effect';

// The image origins a deployment serves from, derived from the same build-time
// env that BUILDS the image URLs.
//
// In each storefront shell, two lists have to agree on this set:
//
//   `next.config.ts` → `images.remotePatterns`, which decides whether the
//   optimizer may fetch a remote URL at all;
//   `src/next/VsImage.tsx` → the bypass rules, which decide the optimizer is
//   skipped and the browser loads the URL directly.
//
// A host present in neither is not an error anything can catch. `/_next/image`
// answers 400, the `<img>` fires `error`, and `useImageFallbackFlow` records a
// dead image — the missing-image placeholder — without a single request having
// reached that host. The failure is invisible while every configured filter has
// a pre-generated CDN variant, and surfaces the moment one does not: the
// CDN-miss fallback (`getImageBackendFallback`, rewriting a 404 to the backend's
// on-demand `resolve` URL) is exactly the path an unregistered host kills.
//
// Deriving both lists from the env removes the class rather than the instance:
// an environment cannot be half-registered, because registration is no longer a
// separate act from configuring it.
//
// Both keys use URL validation. The API module adds a brand to its origin schema.
// This module accepts absent image configuration keys. It cannot import the API module here.
//
// `Config.option` makes an ABSENT key `None` (local development runs with no
// CDN) while a PRESENT-but-invalid one still fails. That failure is deliberate:
// `next.config.ts` reads this module, so a malformed origin stops the build
// instead of shipping an app whose every image URL is malformed. It also makes
// the shells' module-scope reads safe on the client — the browser only ever
// sees literals a build already validated.
//
// The values arrive as an explicit record. Each shell reads them with literal
// `process.env.NEXT_PUBLIC_*` accesses in its own `src/config/imageOrigins.ts`:
// Next inlines those at build time and does not inline dynamic lookups.
//
// Reading them there rather than through `RuntimeClientConfig` is what lets both
// consumers have a value at MODULE scope: `next.config.ts` before any bundler
// exists, and `VsImage` before any engine shell platform is installed.
//
// This module belongs to the config-time graph, so it imports nothing but
// `effect`. Next's config loader leaves a workspace package's `.ts` module to
// Node's own TypeScript loader, which cannot resolve an extensionless relative
// import such as `../config/keys`. The env key names are inlined for that
// reason.
const GCS_PUBLIC_URL = 'NEXT_PUBLIC_GCS_PUBLIC_URL';
const API_ENTRYPOINT = 'NEXT_PUBLIC_API_ENTRYPOINT';

/**
 * Path prefix under which the backend generates a missing Liip variant on
 * demand. Only this subtree of the API entrypoint serves images, so the bypass
 * rule is scoped to it rather than to the whole host.
 */
export const BACKEND_RESOLVE_PATH_PREFIX = '/media/cache/resolve';

/**
 * Structural mirror of Next's `RemotePattern`, declared locally to keep this
 * module importable before the bundler exists.
 */
export type ImageRemotePattern = {
  readonly protocol: 'http' | 'https';
  readonly hostname: string;
  readonly port: string;
  readonly pathname: string;
};

export type ImageOriginEnv = {
  readonly cdnUrl: string | undefined;
  readonly apiEntrypoint: string | undefined;
};

export type ImageOrigins = {
  /** Host serving pre-generated Liip variants; `null` when no CDN is configured. */
  readonly cdnHostname: string | null;
  /** Host serving the on-demand `resolve` fallback; `null` when unconfigured. */
  readonly backendHostname: string | null;
  /**
   * `remotePatterns` entries for whichever of the two are configured. Empty when
   * neither is — which is why `next.config.ts` keeps its static entries as a
   * floor instead of replacing them with these.
   */
  readonly remotePatterns: readonly ImageRemotePattern[];
};

// `Schema.URL` accepts any scheme `globalThis.URL` parses; `remotePatterns`
// speaks only http and https. The narrowing happens once, here, and decides
// both outputs together: an origin the optimizer could never be configured for
// is not an image origin, so it contributes neither a pattern nor a bypass
// host. Doing it in one place is what keeps the two lists from disagreeing.
const toImageOrigin = (
  url: URL
): Option.Option<{ readonly hostname: string; readonly pattern: ImageRemotePattern }> => {
  const protocol = url.protocol === 'https:' ? 'https' : url.protocol === 'http:' ? 'http' : null;

  if (protocol === null) return Option.none();

  return Option.some({
    hostname: url.hostname,
    pattern: {
      protocol,
      hostname: url.hostname,
      port: url.port,
      // Not the origin's own path: an origin's images hang off prefixes it does
      // not itself name (the CDN prefix carries `/public`, variants sit under
      // `/cache/<filter>/…`), and narrowing would re-introduce the same
      // silent-400 failure one level down.
      pathname: '/**',
    },
  });
};

/**
 * The origins as a `Config`, so the descriptors — not a hand-rolled parse —
 * decide what a valid origin is. Yieldable like any other config here.
 */
export const ImageOriginsConfig = Effect.gen(function* () {
  const cdnUrl = yield* Config.option(Config.URL(GCS_PUBLIC_URL));
  const backendUrl = yield* Config.option(Config.URL(API_ENTRYPOINT));

  const cdn = Option.flatMap(cdnUrl, toImageOrigin);
  const backend = Option.flatMap(backendUrl, toImageOrigin);

  const hostnameOf = (origin: typeof cdn): string | null =>
    Option.getOrNull(Option.map(origin, (value) => value.hostname));

  return {
    cdnHostname: hostnameOf(cdn),
    backendHostname: hostnameOf(backend),
    remotePatterns: [cdn, backend].flatMap((origin) =>
      Option.match(origin, { onNone: () => [], onSome: (value) => [value.pattern] })
    ),
  } satisfies ImageOrigins;
});

/**
 * Sync wrapper over an explicit env record, mirroring `getNextOutputSync`. Takes
 * the record rather than reading `process.env` so the mapping is pinned by a
 * test instead of by whichever environment a build happens to run in.
 *
 * Throws when a key is present but is not a valid URL — see the module note.
 */
export function deriveImageOrigins(env: ImageOriginEnv): ImageOrigins {
  const record: Record<string, string> = {
    ...(env.cdnUrl === undefined ? {} : { [GCS_PUBLIC_URL]: env.cdnUrl }),
    ...(env.apiEntrypoint === undefined ? {} : { [API_ENTRYPOINT]: env.apiEntrypoint }),
  };

  return Effect.runSync(
    Effect.provide(
      ImageOriginsConfig,
      ConfigProvider.layer(ConfigProvider.fromEnv({ env: record }))
    )
  );
}
